// tab/src/components/SupervisorView/SupervisorView.tsx
// OnRounds Phase 2 - Supervisor Dashboard Component

import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  Text,
  Badge,
  Avatar,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  MessageBar,
  MessageBarBody,
  Divider,
} from '@fluentui/react-components';
import {
  Eye24Regular,
  People24Regular,
  Mail24Regular,
  Warning24Regular,
  Clock24Regular,
  Checkmark24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import { useTeam } from '../../context/TeamContext';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    ...shorthands.gap('16px'),
  },
  statCard: {
    ...shorthands.padding('20px'),
    textAlign: 'center',
  },
  statValue: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  statGreen: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statRed: {
    color: tokens.colorPaletteRedForeground1,
  },
  statYellow: {
    color: tokens.colorPaletteYellowForeground2,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  roleTable: {
    width: '100%',
  },
  statusBadge: {
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
  },
  statusOccupied: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  statusVacant: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  statusHandover: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    maxHeight: '400px',
    ...shorthands.overflow('auto'),
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
  },
  activityIcon: {
    ...shorthands.padding('8px'),
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  activityContent: {
    flexGrow: 1,
  },
  activityTime: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  alertCard: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorPaletteRedBackground2,
    ...shorthands.borderRadius('8px'),
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  warningCard: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorPaletteYellowBackground2,
    ...shorthands.borderRadius('8px'),
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
});

interface DashboardData {
  teamName: string;
  summary: {
    totalRoles: number;
    occupiedRoles: number;
    vacantRoles: number;
    handoverPending: number;
    unreadMessages: number;
    criticalMessages: number;
  };
  roles: Array<{
    roleId: string;
    roleName: string;
    status: 'vacant' | 'occupied' | 'handover_pending';
    userDisplayName: string | null;
    clockOnTime: string | null;
    unreadMessageCount: number;
    role: {
      category: string;
      color: string;
    };
  }>;
  recentActivity: Array<{
    id: string;
    action: string;
    userDisplayName: string;
    timestamp: string;
    details: any;
  }>;
}

export const SupervisorView: React.FC = () => {
  const styles = useStyles();
  const { currentTeam } = useTeam();
  const { user } = useUser();

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await api.getSupervisorDashboard(currentTeam.id);
      setDashboard(data);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam]);

  useEffect(() => {
    loadDashboard();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'clock_on':
        return <Clock24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />;
      case 'clock_off':
        return <Clock24Regular style={{ color: tokens.colorNeutralForeground2 }} />;
      case 'message_sent':
        return <Mail24Regular style={{ color: tokens.colorBrandForeground1 }} />;
      case 'message_read':
        return <Checkmark24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />;
      default:
        return <Eye24Regular />;
    }
  };

  const getActionText = (action: string, details: any) => {
    switch (action) {
      case 'clock_on':
        return `Clocked on as ${details?.roleName || 'role'}`;
      case 'clock_off':
        return `Clocked off from ${details?.roleName || 'role'}`;
      case 'message_sent':
        return `Sent message to ${details?.toRoleName || 'role'}`;
      case 'message_read':
        return `Read message: ${details?.subject || ''}`;
      case 'message_handover':
        return `Acknowledged handover message`;
      default:
        return action.replace(/_/g, ' ');
    }
  };

  if (!user?.isSupervisor && !user?.isAdmin) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          You don't have permission to view the supervisor dashboard.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (isLoading && !dashboard) {
    return <Loading message="Loading supervisor dashboard..." />;
  }

  if (!dashboard) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {error || 'Failed to load dashboard data'}
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Text size={500} weight="semibold">Supervisor Dashboard</Text>
          <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
            {dashboard.teamName}
          </Text>
        </div>
        <Button
          icon={<ArrowClockwise24Regular />}
          appearance="subtle"
          onClick={loadDashboard}
        >
          Refresh
        </Button>
      </div>

      {/* Alerts */}
      {dashboard.summary.criticalMessages > 0 && (
        <div className={styles.alertCard}>
          <Warning24Regular style={{ color: tokens.colorPaletteRedForeground1, fontSize: '24px' }} />
          <div>
            <Text weight="semibold">Critical Messages Pending</Text>
            <Text size={200} style={{ display: 'block' }}>
              {dashboard.summary.criticalMessages} critical message(s) require attention
            </Text>
          </div>
        </div>
      )}

      {dashboard.summary.vacantRoles > 0 && (
        <div className={styles.warningCard}>
          <People24Regular style={{ color: tokens.colorPaletteYellowForeground2, fontSize: '24px' }} />
          <div>
            <Text weight="semibold">Vacant Roles</Text>
            <Text size={200} style={{ display: 'block' }}>
              {dashboard.summary.vacantRoles} role(s) currently have no one assigned
            </Text>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <Text className={styles.statValue}>{dashboard.summary.totalRoles}</Text>
          <Text className={styles.statLabel}>Total Roles</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={`${styles.statValue} ${styles.statGreen}`}>
            {dashboard.summary.occupiedRoles}
          </Text>
          <Text className={styles.statLabel}>Occupied</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statValue}>
            {dashboard.summary.vacantRoles}
          </Text>
          <Text className={styles.statLabel}>Vacant</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={`${styles.statValue} ${styles.statYellow}`}>
            {dashboard.summary.handoverPending}
          </Text>
          <Text className={styles.statLabel}>Handover Pending</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statValue}>
            {dashboard.summary.unreadMessages}
          </Text>
          <Text className={styles.statLabel}>Unread Messages</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={`${styles.statValue} ${dashboard.summary.criticalMessages > 0 ? styles.statRed : ''}`}>
            {dashboard.summary.criticalMessages}
          </Text>
          <Text className={styles.statLabel}>Critical</Text>
        </Card>
      </div>

      {/* Role Status Table */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <People24Regular />
          <Text size={400} weight="semibold">Current Role Status</Text>
        </div>
        <Card>
          <Table className={styles.roleTable}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Current Holder</TableHeaderCell>
                <TableHeaderCell>Since</TableHeaderCell>
                <TableHeaderCell>Messages</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dashboard.roles.map((role) => (
                <TableRow key={role.roleId}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: role.role?.color || '#6B7280',
                        }}
                      />
                      <Text weight="semibold">{role.roleName}</Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`${styles.statusBadge} ${
                        role.status === 'occupied' ? styles.statusOccupied :
                        role.status === 'handover_pending' ? styles.statusHandover :
                        styles.statusVacant
                      }`}
                    >
                      {role.status === 'occupied' ? 'Occupied' :
                       role.status === 'handover_pending' ? 'Handover' :
                       'Vacant'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {role.userDisplayName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar name={role.userDisplayName} size={24} />
                        <Text>{role.userDisplayName}</Text>
                      </div>
                    ) : (
                      <Text style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    {role.clockOnTime ? (
                      <Text size={200}>
                        {formatDistanceToNow(new Date(role.clockOnTime), { addSuffix: true })}
                      </Text>
                    ) : (
                      <Text style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    {role.unreadMessageCount > 0 ? (
                      <Badge appearance="filled" color="danger">
                        {role.unreadMessageCount}
                      </Badge>
                    ) : (
                      <Text style={{ color: tokens.colorNeutralForeground3 }}>0</Text>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Clock24Regular />
          <Text size={400} weight="semibold">Recent Activity</Text>
        </div>
        <Card>
          <div className={styles.activityList}>
            {dashboard.recentActivity.length === 0 ? (
              <Text style={{ padding: '20px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                No recent activity
              </Text>
            ) : (
              dashboard.recentActivity.map((activity) => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon}>
                    {getActionIcon(activity.action)}
                  </div>
                  <div className={styles.activityContent}>
                    <Text weight="semibold">{activity.userDisplayName}</Text>
                    <Text size={200} style={{ display: 'block' }}>
                      {getActionText(activity.action, activity.details)}
                    </Text>
                  </div>
                  <Text className={styles.activityTime}>
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </Text>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
